/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { cpf as cpfValidator, cnpj as cnpjValidator } from 'cpf-cnpj-validator';

import api from '../../../services/api';

import getValidationErrors from '../../../utils/getValidationError';
import { useToast } from '../../../context/ToastContext';

import Loading from '../../../components/Loading';
import Input from '../../../components/Input';
import CheckboxInput from '../../../components/CheckboxInput';
import SimpleSelect from '../../../components/SimpleSelect';
import SelectAnimated from '../../../components/SelectAnimated';
import Button from '../../../components/Button';

import ufList from '../../../data/ufList.json';

import { IMaterial, IMaterialResponse } from '../../../interfaces/material';

import {
  Container,
  Content,
  Split,
  SplitLeft,
  VerticalLine,
  SplitRight,
  CityAndUF,
  Phones,
  Materials,
} from './styles';

import { IProvider } from '../../../interfaces/provider';
import InputHidden from '../../../components/InputHidden';
import InputPhone from '../../../components/InputPhone';
import convertDateStringToDate from '../../../utils/convertDateStringToDate';

interface IProviderDTO extends Omit<IProvider, 'cpfCnpj'> {
  cpfCnpj?: string;
}

const UpdateProvider: React.FC = () => {
  const { id }: any = useParams();
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [listMaterials, setListMaterials] = useState<IMaterial[]>([]);

  const [saving, setSaving] = useState<boolean>(false);
  const [loadingMaterials, setLoadingMaterials] = useState<boolean>(false);

  const [provider, setProvider] = useState<IProvider>({} as IProvider);

  useEffect(() => {
    const getProvider = async (): Promise<void> => {
      try {
        setLoadingMaterials(true);

        const responseProvider: any = await api.get(`providers/${id}`);

        // Formata o array de materials vindo do banco para selecionar os item no select
        // de materiais.
        let materialsIdFormatted: Array<IMaterialResponse> = [];
        if (
          responseProvider.data.materials &&
          responseProvider.data.materials.length > 0
        ) {
          materialsIdFormatted = responseProvider.data.materials.map(
            (i: IMaterialResponse) => ({
              value: i.id,
              label: i.name,
            }),
          );
        }
        // Fim materialsIdFormatted

        const newResponseProviderFormatted: IProvider = {
          ...responseProvider.data,
          cpfCnpj:
            responseProvider.data.cpfCnpj.length === 14
              ? cnpjValidator.format(responseProvider.data.cpfCnpj)
              : cpfValidator.format(responseProvider.data.cpfCnpj),
          createdAt: convertDateStringToDate(responseProvider.data.createdAt),
          updatedAt: convertDateStringToDate(responseProvider.data.updatedAt),

          materialsId: materialsIdFormatted,
        };

        setProvider(newResponseProviderFormatted);

        formRef.current?.setData(newResponseProviderFormatted);

        // Busca a lista de materiais
        const responseMaterials: any = await api.get('materials/active');

        const responseMaterialsFormatted = responseMaterials.data.map(
          (i: IMaterialResponse) => ({
            value: i.id,
            label: i.name,
          }),
        );

        setListMaterials(responseMaterialsFormatted);

        setLoadingMaterials(false);
      } catch (error: any) {
        setLoadingMaterials(false);

        // eslint-disable-next-line no-shadow
        if (error.response) {
          const { data } = error.response; // Error vindo do back está em data dentro de response
          addToast({
            type: 'error',
            title: 'Ocorreu um erro!',
            description: `Erro: ${data.message}`,
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Ocorreu um erro interno!',
          description: `Error: ${error}`,
        });
      }
    };
    getProvider();
  }, [addToast, id]);

  const handleFormSubmit = useCallback(
    async (dataForm: IProvider, { reset }) => {
      try {
        setSaving(true);

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          companyName: Yup.string().required(
            'O Nome do Fornecedor é obrigatório',
          ),
          fantasyName: Yup.string().required('O Nome Fantasia é obrigatório'),
          address: Yup.string().required('O Endereço é obrigatório'),
          neighborhood: Yup.string().required('O Bairro é obrigatório'),
          city: Yup.string().required('A Cidade é obrigatória'),
          uf: Yup.string().required('UF obrigatório'),
          mainPhone: Yup.string().required('O Telefone é obrigatório'),
        });

        await schema.validate(dataForm, { abortEarly: false });

        const newData: IProviderDTO = {
          ...dataForm,
          id: parseInt(id, 10),
        };

        delete newData.cpfCnpj;
        await api.put('providers', newData);

        addToast({
          type: 'success',
          title: 'Atualização realizada com sucesso',
        });

        reset();
        setSaving(false);

        navigate('/providers');
      } catch (error: any) {
        setSaving(false);

        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);

          return;
        }

        if (error.response) {
          const { data } = error.response; // Error vindo do back está em data dentro de response

          addToast({
            type: 'error',
            title: 'Erro na atualização',
            description: data.message,
          });
          if (
            formRef.current?.getFieldRef(data.field) &&
            data.field.toString().indexOf('_id') === -1
          ) {
            formRef.current?.getFieldRef(data.field).focus(); // Foca o campo que deu erro de acordo com o field retornado do back
          }

          return;
        }

        addToast({
          type: 'error',
          title: 'Erro ao atualizar!',
          description: `Error: ${error}`,
        });
      }
    },
    [addToast, navigate, id],
  );

  return (
    <Container>
      <Content>
        <header>
          <p>Atualização de Fornecedores</p>
        </header>
        <Form ref={formRef} onSubmit={handleFormSubmit} initialData={provider}>
          <InputHidden type="hidden" name="id" />
          <Split>
            <SplitLeft>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                labelText="Razão Social*"
                disabled={saving}
              />
              <Input
                id="fantasyName"
                name="fantasyName"
                type="text"
                labelText="Nome Fantasia*"
                disabled={saving}
              />
              <Input
                id="cpfCnpj"
                name="cpfCnpj"
                type="text"
                labelText="CPF/CNPJ*"
                disabled
              />
              <Input
                id="contactName"
                name="contactName"
                type="text"
                labelText="Contato"
                disabled={saving}
              />
              <Input
                id="address"
                name="address"
                type="text"
                labelText="Endereço"
                disabled={saving}
              />
            </SplitLeft>

            <VerticalLine />

            <SplitRight>
              <Input
                id="neighborhood"
                name="neighborhood"
                type="text"
                labelText="Bairro"
                disabled={saving}
              />
              <CityAndUF>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  labelText="Cidade"
                  disabled={saving}
                />
                <SimpleSelect
                  id="uf"
                  name="uf"
                  labelText="UF"
                  placeholder=""
                  options={ufList}
                  getOptionLabel={option => option.value}
                  disabled={saving}
                />
              </CityAndUF>
              <Phones>
                <InputPhone
                  id="mainPhone"
                  name="mainPhone"
                  type="text"
                  labelText="Telefone"
                  disabled={saving}
                />
                <InputPhone
                  id="secondaryPhone"
                  name="secondaryPhone"
                  type="text"
                  labelText="WhatsApp"
                  disabled={saving}
                />
              </Phones>
              <Input
                id="email"
                name="email"
                type="text"
                labelText="E-mail"
                disabled={saving}
              />
              <CheckboxInput
                id="isActive"
                name="isActive"
                label="Ativo?"
                position="left"
                disabled={saving}
              />
            </SplitRight>
          </Split>

          {loadingMaterials ? (
            <p>carregando ...</p>
          ) : (
            <Materials>
              <span>Materiais nesse Fornecedor</span>
              <SelectAnimated
                id="materialsId"
                name="materialsId"
                placeholder=""
                options={listMaterials}
                isMulti
                disabled={saving}
              />
            </Materials>
          )}

          <Button type="submit" style={{ width: '233px' }} disabled={saving}>
            {saving ? <Loading size={24} color="white" /> : 'Salvar'}
          </Button>
        </Form>
      </Content>
    </Container>
  );
};

export default UpdateProvider;
